import * as THREE from 'three';
import {GUI} from 'dat.gui';
import {OrbitControls} from "three/examples/jsm/controls/OrbitControls";
import {Vector3, Vector3Tuple} from "three/src/math/Vector3";
import {Mesh} from "three";

const COLOR_BG = '#ffffff';

const COLOR_ELECTRON:string = '#0cc45b';
const COLOR_NEUTRON:string = '#1960ce';
const COLOR_PROTON:string = '#af1823';

const COLOR_ELECTRON_LANE = '#d9d9d9';

const NEUTRON_MATERIAL = new THREE.MeshPhongMaterial({
    color: COLOR_NEUTRON,
    specular: "#111111",
    shininess: 30,
    opacity: 0.90,
    transparent: true
});

const PROTON_MATERIAL = new THREE.MeshPhongMaterial({
    color: COLOR_PROTON,
    specular: "#111111",
    shininess: 30,
    opacity: 0.90,
    transparent: true
});

const ELECTRON_MATERIAL = new THREE.MeshPhongMaterial({
    color: COLOR_ELECTRON,
    specular: "#111111",
    shininess: 20,
});

const ELECTRON_LANE_TORUS_MATERIAL = new THREE.MeshBasicMaterial({color: COLOR_ELECTRON_LANE});

const ANIM_GO_NUTS_AFTER_FRAMES = 200;

/**
 * A class to set up some basic scene elements to minimize code in the
 * main execution file.
 */
export default class BasicScene extends THREE.Scene {

    // A dat.gui class debugger that is added by default
    debugger: GUI = null;

    // Setups a scene camera
    camera: THREE.PerspectiveCamera = null;

    // setup renderer
    renderer: THREE.Renderer = null;

    // setup Orbitals
    orbitals: OrbitControls = null;

    // Holds the lights for easy reference
    lights: Array<THREE.Light> = [];

    // Number of PointLight objects around origin
    lightCount: number = 1;
    // Distance above ground place
    lightDistance: number = 40;

    cameraPosition: number = 40;

    // Get some basic params
    width = 500;
    height = 500;

    canvas: HTMLCanvasElement;

    nucleus: number = 2;

    constructor(canvas: HTMLCanvasElement) {
        super();
        this.canvas = canvas;
    }

    /**
     * Initializes the scene by adding lights, and the geometry
     */
    initialize(debug: boolean = true, addGridHelper: boolean = true, width: number, height: number) {

        this.width = width;
        this.height = height;

        // setup camera
        this.camera = new THREE.PerspectiveCamera(20, this.width / this.height, 1, 2000);
        this.camera.position.z = this.cameraPosition;
        this.camera.position.y = this.cameraPosition;
        this.camera.position.x = this.cameraPosition;

        // setup renderer
        this.renderer = new THREE.WebGLRenderer({
            canvas: this.canvas,
            alpha: true,
            antialias: true
        });
        this.renderer.setSize(this.width, this.height);

        // add window resizing
        BasicScene.addWindowResizing(this.camera, this.renderer);

        // sets up the camera's orbital controls
        this.orbitals = new OrbitControls(this.camera, this.renderer.domElement)
        this.orbitals.minDistance = 10;
        this.orbitals.maxDistance = 200;

        // Adds an origin-centered grid for visual reference
        if (addGridHelper) {

            // Adds a grid
            this.add(new THREE.GridHelper(10, 10, 'red'));

            // Adds an axis-helper
            this.add(new THREE.AxesHelper(3))
        }

        // set the background color
        this.background = new THREE.Color(COLOR_BG);

        const light2 = new THREE.HemisphereLight("#f5faf5", "#b4e8e8", 0.8);
        this.add(light2);

        // create the lights
        for (let i = 0; i < this.lightCount; i++) {

            // Positions evenly in a circle pointed at the origin
            const light = new THREE.PointLight(0xffffff, 0.75);
            let lightX = this.lightDistance * Math.sin(Math.PI * 2 / this.lightCount * i);
            let lightZ = this.lightDistance * Math.cos(Math.PI * 2 / this.lightCount * i);

            // Create a light
            light.position.set(lightX, this.lightDistance, lightZ)
            light.lookAt(0, 0, 0)
            this.add(light);
            this.lights.push(light);

            // Visual helpers to indicate light positions
            if (addGridHelper) {
                this.add(new THREE.PointLightHelper(light, .5, 0xff9900));
            }
        }

    }

    public setElement(protons: number, neutrons: number, lanes: number, electrons: number[]) {

        this.doAnimate = false;

        this.remove(...this.atomicNucleus);
        this.remove(...this.electronToruses);
        this.remove(...this.electronsGroups);

        this.electronToruses = [];
        this.electrons = [];
        this.atomicNucleus = [];

        let nrad = this.addNucleus(protons, neutrons);
        let lanesdist = 8 / lanes;

        for (let i = 0; i < lanes; i++) {
            this.addElectrons(i, 11 - i * lanesdist, electrons[i], nrad / 3);
        }

        this.orbitals.reset();

        this.camera.updateProjectionMatrix();

        this.renderer.render(this, this.camera);

        this.makeElectronsNuts = false;
        this.rotates = 0;
        this.animFrame = 0;

        this.doAnimate = true;
    }

    private atomicNucleus: Mesh[] = [];

    private addNucleus(protons: number, neutrons: number) {
        let nrad = (2.55 * (1 + (protons + neutrons) / 420)) / Math.sqrt(((protons + neutrons) / 2));
        let d = 1 + (protons + neutrons) / 260;
        nrad = Math.min(1, nrad);

        const geometry = new THREE.SphereGeometry(nrad, 50, 50);

        let arr = [];

        if (protons == 1 && neutrons == 0) {
            let elem = new THREE.Mesh(geometry, PROTON_MATERIAL);
            elem.position.set(0, 0, 0);
            this.add(elem);
            this.atomicNucleus.push(elem);
            return nrad;
        }


        for (let i = 0; i < protons; i++) {
            arr.push(0);
        }
        for (let i = 0; i < neutrons; i++) {
            arr.push(1);
        }

        //if (protons + neutrons > 10) {
        arr = BasicScene.shuffleArrayElements(arr);
        //}

        let points = BasicScene.fibonacciSphere(arr.length);

        this.atomicNucleus = [];
        for (let i = 0; i < points.length; i++) {

            let elem: THREE.Mesh;
            if (arr[i] == 0) {
                elem = new THREE.Mesh(geometry, PROTON_MATERIAL);
            } else {
                elem = new THREE.Mesh(geometry, NEUTRON_MATERIAL);
            }

            let pos = points[i];
            elem.position.set(pos.x * d, pos.y * d, pos.z * d);
            this.add(elem);
            this.atomicNucleus.push(elem);
        }

        //this.addElectrons(6, 2);
        //this.addElectrons(8, 4);
        //this.addElectrons(0, 10, 8);

        return nrad;
    }


    private electrons: THREE.Mesh[][] = [];
    private electronsGroups: THREE.Group[] = [];
    private electronToruses: THREE.Mesh[] = [];

    private addElectrons(lane: number, distanceFromCenter: number, numberOfElectrons: number, electrorad = 0.28) {

        const torusGeometry = new THREE.TorusGeometry(distanceFromCenter, 0.02, 16, 100);
        const torus = new THREE.Mesh(torusGeometry, ELECTRON_LANE_TORUS_MATERIAL);
        torusGeometry.rotateX(Math.PI * 0.5)

        this.electronToruses[lane] = torus;
        this.add(torus)

        let arc = (2 * Math.PI) / numberOfElectrons;

        const electronGeometry = new THREE.SphereGeometry(electrorad, 50, 50);

        this.electrons[lane] = [];
        for (let i = 0; i < numberOfElectrons; i++) {

            //let emesh = new THREE.Mesh(electronGeometry, BasicScene.electronMaterial);
            let emesh = new THREE.Mesh(electronGeometry, ELECTRON_MATERIAL);

            emesh.position.set(
                distanceFromCenter * Math.cos(i * arc),
                0,
                distanceFromCenter * Math.sin(i * arc)
            );

            //emesh.rotateOnWorldAxis(BasicScene.worldAxis, THREE.MathUtils.degToRad(22))
            this.electrons[lane].push(emesh);

            //this.add(emesh);
        }


        this.electronsGroups = [];
        for (const earr of this.electrons) {
            let g = new THREE.Group();

            for (const elem of earr) {
                g.add(elem);
            }
            this.electronsGroups.push(g);
            this.add(g);
        }

    }


    private rotates: number = 0;
    private makeElectronsNuts = false;
    private doAnimate = true;
    private animFrame: number = 0;

    public animate() {

        if (!this.doAnimate) {
            return;
        }

        let ARC = 0.005;

        if(this.makeElectronsNuts){
            this.rotates += ARC;

            if(this.rotates >= Math.PI*2){
                this.makeElectronsNuts = false;
                this.rotates = 0;
            }

        }else{

            this.animFrame++;
            if(this.animFrame > ANIM_GO_NUTS_AFTER_FRAMES){
                this.makeElectronsNuts = true;
                this.animFrame = 0;
            }

        }

        for (var i = 0; i < this.electronToruses.length; i++) {

            let rotateVector = new Vector3(0, 0, 0);

            if(this.makeElectronsNuts) {
                if (i % 4 == 0) {
                    rotateVector = new Vector3(-1, 0, 0);
                }

                if (i % 4 == 1) {
                    rotateVector = new Vector3(1, 0, 0);
                }

                if (i % 4 == 2) {
                    rotateVector = new Vector3(0, 0, 1);
                }

                if (i % 4 == 3) {
                    rotateVector = new Vector3(0, 0, -1);
                }

                this.electronToruses[i].rotateOnWorldAxis(rotateVector, ARC)
                this.electronsGroups[i].rotateOnWorldAxis(rotateVector, ARC)
            }

            this.electronsGroups[i].rotateOnAxis(new Vector3(0, 1, 0), 0.015)

        }

    }

    /**
     * Given a ThreeJS camera and renderer, resizes the scene if the
     * browser window is resized.
     * @param camera - a ThreeJS PerspectiveCamera object.
     * @param renderer - a subclass of a ThreeJS Renderer object.
     */
    static addWindowResizing(camera: THREE.PerspectiveCamera, renderer: THREE.Renderer) {
        window.addEventListener('resize', onWindowResize, false);

        function onWindowResize() {


            if(window.innerWidth < 1140){
                let cw = window.innerWidth * 0.33;
                camera.aspect = 1;
                camera.updateProjectionMatrix();
                renderer.setSize(cw, cw);
            }

        }

    }


    static fibonacciSphere(samples: number): Vector3[] {

        let points: Vector3[] = [];

        //golden angle in radians
        let phi = Math.PI * (3.0 - Math.sqrt(5.0));

        for (let i = 0; i < samples; i++) {
            let y = 1 - (i / (samples - 1.0)) * 2;  //y goes from 1 to -1

            let radius = Math.sqrt(1 - y * y);

            let theta = phi * i; //golden angle increment

            let x = Math.cos(theta) * radius;
            let z = Math.sin(theta) * radius;

            points.push(
                new Vector3(x, y, z)
            );
        }

        return points;
    }


    static shuffleArrayElements<T>(array: T[]): T[] {
        let currentIndex = array.length, randomIndex;

        // While there remain elements to shuffle.
        while (currentIndex != 0) {

            // Pick a remaining element.
            randomIndex = Math.floor(Math.random() * currentIndex);
            currentIndex--;

            // And swap it with the current element.
            [array[currentIndex], array[randomIndex]] = [
                array[randomIndex], array[currentIndex]];
        }

        return array;
    }

}